/**
 * Definitions PARTAGEES entre l'ecran Maintenance et son module de reglages :
 * catalogues de pastilles, catalogues de stats, cles de prefs et defauts.
 * Un seul endroit, zero duplication.
 *
 * L'app a DEUX sections portees par DEUX collections (signalements = `tickets`,
 * interventions = `workOrders`), donc deux catalogues de chaque. Les prefs
 * suivent les cles standard du socle :
 *  - "app.maintenance.filters" : Record<filterId, boolean>, visibilite des
 *    pastilles des DEUX sections, fusionnee dans une seule cle (les ids sont
 *    prefixes "signalements." / "interventions." pour ne jamais se croiser) ;
 *  - "app.maintenance.stats.signalements" et "app.maintenance.stats.interventions" :
 *    string[], ids ordonnes des stats d'en-tete de chaque section (max 3) ;
 *  - "app.maintenance.laborRate" : number, le taux horaire de main d'oeuvre.
 */

/* ------------------------------------------------------------------ */
/* Section SIGNALEMENTS                                                */
/* ------------------------------------------------------------------ */

/** Ids des pastilles de la section Signalements. */
export type TicketFilterId = 'tous' | 'ouvert' | 'en_cours' | 'resolu';

export const TICKET_FILTER_DEFS: ReadonlyArray<{ id: TicketFilterId; label: string }> = [
  { id: 'tous', label: 'Tous' },
  { id: 'ouvert', label: 'Ouvert' },
  { id: 'en_cours', label: 'En cours' },
  { id: 'resolu', label: 'Resolu' },
];

/** Pastille de repli quand la pastille active est masquee dans les reglages. */
export const DEFAULT_TICKET_FILTER_ID: TicketFilterId = 'tous';

/** Ids des stats disponibles pour l'en-tete de la section Signalements. */
export type TicketStatId = 'total' | 'ouverts' | 'urgents' | 'imputables-client';

export const TICKET_STAT_DEFS: ReadonlyArray<{ id: TicketStatId; label: string }> = [
  { id: 'total', label: 'signalements' },
  { id: 'ouverts', label: 'ouverts' },
  { id: 'urgents', label: 'urgents' },
  { id: 'imputables-client', label: 'imputables client' },
];

/** Defaut : ouverts, urgents, imputables client (le total se lit dans le menu). */
export const DEFAULT_TICKET_STATS: string[] = ['ouverts', 'urgents', 'imputables-client'];

/** Cle de pref des stats de la section Signalements. */
export const TICKETS_STATS_PREF_KEY = 'app.maintenance.stats.signalements';

/* ------------------------------------------------------------------ */
/* Section INTERVENTIONS                                               */
/* ------------------------------------------------------------------ */

/** Ids des pastilles de la section Interventions. */
export type WorkOrderFilterId = 'toutes' | 'a_planifier' | 'en_cours' | 'termine';

export const WORK_ORDER_FILTER_DEFS: ReadonlyArray<{ id: WorkOrderFilterId; label: string }> = [
  { id: 'toutes', label: 'Toutes' },
  { id: 'a_planifier', label: 'A planifier' },
  { id: 'en_cours', label: 'En cours' },
  { id: 'termine', label: 'Terminees' },
];

/** Pastille de repli de la section Interventions. */
export const DEFAULT_WORK_ORDER_FILTER_ID: WorkOrderFilterId = 'toutes';

/** Ids des stats disponibles pour l'en-tete de la section Interventions. */
export type WorkOrderStatId = 'total' | 'en-cours' | 'cout-mois' | 'a-refacturer';

export const WORK_ORDER_STAT_DEFS: ReadonlyArray<{ id: WorkOrderStatId; label: string }> = [
  { id: 'total', label: 'interventions' },
  { id: 'en-cours', label: 'en cours' },
  { id: 'cout-mois', label: 'ce mois-ci' },
  { id: 'a-refacturer', label: 'a refacturer' },
];

/** Defaut : en cours, cout du mois, montant a refacturer. */
export const DEFAULT_WORK_ORDER_STATS: string[] = ['en-cours', 'cout-mois', 'a-refacturer'];

/** Cle de pref des stats de la section Interventions. */
export const WORK_ORDERS_STATS_PREF_KEY = 'app.maintenance.stats.interventions';

/* ------------------------------------------------------------------ */
/* Cles communes                                                       */
/* ------------------------------------------------------------------ */

/** Nombre maximal de stats affichees (contrat de la cle "app.<id>.stats"). */
export const STATS_MAX = 3;

/** Cle de pref : visibilite des pastilles des DEUX sections. Cle absente = visible. */
export const FILTERS_PREF_KEY = 'app.maintenance.filters';

/** Id de pref d'une pastille de la section Signalements. */
export function ticketFilterPrefId(id: string): string {
  return `signalements.${id}`;
}

/** Id de pref d'une pastille de la section Interventions. */
export function workOrderFilterPrefId(id: string): string {
  return `interventions.${id}`;
}

/** Defaut filtres : toutes les pastilles des deux sections visibles. */
export const DEFAULT_FILTERS: Record<string, boolean> = {
  ...Object.fromEntries(TICKET_FILTER_DEFS.map((def) => [ticketFilterPrefId(def.id), true])),
  ...Object.fromEntries(
    WORK_ORDER_FILTER_DEFS.map((def) => [workOrderFilterPrefId(def.id), true]),
  ),
};

/**
 * Cle de pref du TAUX HORAIRE de main d'oeuvre, en euros. C'est un reglage de
 * l'app, jamais une donnee en base : changer le taux ne reecrit aucune
 * intervention, il change seulement la facon dont on chiffre la main d'oeuvre.
 */
export const LABOR_RATE_PREF_KEY = 'app.maintenance.laborRate';

/** Taux horaire par defaut (EUR). */
export const DEFAULT_LABOR_RATE = 65;
